/** Stałe Cloudflare zweryfikowane wobec dokumentacji (sierpień 2026). */

export const PLANS = ['free', 'pro', 'business', 'enterprise'] as const;
export type Plan = (typeof PLANS)[number];

/** Porządek planów — do porównań „czy plan klienta wystarcza". */
export const PLAN_RANK: Readonly<Record<Plan, number>> = {
  free: 0,
  pro: 1,
  business: 2,
  enterprise: 3,
};

export function planAtLeast(actual: Plan, required: Plan): boolean {
  return PLAN_RANK[actual] >= PLAN_RANK[required];
}

/** Fazy Ruleset Engine, których dotykamy. */
export const PHASE = {
  firewallCustom: 'http_request_firewall_custom',
  firewallManaged: 'http_request_firewall_managed',
} as const;
export type Phase = (typeof PHASE)[keyof typeof PHASE];

/**
 * ID managed rulesetów. Są to globalne stałe Cloudflare — te same dla każdej
 * strefy i konta.
 *
 * ALE: i tak rozwiązujemy je w runtime przez listowanie rulesetów strefy.
 * Powodem nie jest zmienność ID, tylko ENTITLEMENT — ruleset, do którego
 * strefa nie ma uprawnień, objawia się jako 404 (nie 403), a chcemy to wykryć
 * w `preflight`, a nie w połowie aplikowania.
 */
export const MANAGED_RULESETS = {
  cloudflareFree: {
    id: '77454fe2d30c4220b5701f6fdfb893ba',
    namePl: 'Podstawowy filtr znanych ataków',
    cfName: 'Cloudflare Free Managed Ruleset',
    minPlan: 'free',
  },
  cloudflareManaged: {
    id: 'efb7b8c949ac4650a09736fc376e9aee',
    namePl: 'Rozszerzony filtr znanych ataków',
    cfName: 'Cloudflare Managed Ruleset',
    minPlan: 'pro',
  },
  owaspCore: {
    id: '4814384a9e5d4991b9815dcfc25d2f1f',
    namePl: 'Filtr OWASP',
    cfName: 'Cloudflare OWASP Core Ruleset',
    minPlan: 'pro',
  },
} as const satisfies Record<string, { id: string; namePl: string; cfName: string; minPlan: Plan }>;

export type ManagedRulesetKey = keyof typeof MANAGED_RULESETS;

/**
 * Maksymalna liczba reguł custom w fazie http_request_firewall_custom, PER STREFA.
 * Limit jest DZIELONY z regułami, które klient stworzył sam — przed dodaniem
 * czegokolwiek trzeba policzyć istniejące.
 */
export const CUSTOM_RULE_LIMITS: Readonly<Record<Plan, number>> = {
  free: 5,
  pro: 20,
  business: 100,
  enterprise: 1000,
};

/** Regex (`matches`) w wyrażeniach jest niedostępny poniżej Business. */
export const REGEX_MIN_PLAN: Plan = 'business';

/**
 * Namespace na wszystkim, co tworzymy w koncie klienta. Bez tego nie
 * odróżnimy naszych reguł od jego i rollback jest niemożliwy.
 */
export const RULE_DESCRIPTION_PREFIX = '[cfolks]';
export const RULE_REF_PREFIX = 'cfolks_';

export function ourRuleRef(slug: string): string {
  return `${RULE_REF_PREFIX}${slug}`;
}

export function isOurRule(rule: { ref?: string | undefined }): boolean {
  return typeof rule.ref === 'string' && rule.ref.startsWith(RULE_REF_PREFIX);
}

/** Endpointy Cloudflare. */
export const CF_API_BASE = 'https://api.cloudflare.com/client/v4';

export const CF_OAUTH = {
  authorize: 'https://dash.cloudflare.com/oauth2/auth',
  token: 'https://dash.cloudflare.com/oauth2/token',
  revoke: 'https://dash.cloudflare.com/oauth2/revoke',
  userinfo: 'https://dash.cloudflare.com/oauth2/userinfo',
  jwks: 'https://dash.cloudflare.com/.well-known/jwks.json',
} as const;

/** Globalny limit API: 1200 żądań / 5 minut, kumulatywnie na użytkownika. */
export const CF_RATE_LIMIT = { requests: 1200, windowSeconds: 300 } as const;
